import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import DeleteIcon from '@mui/icons-material/Delete';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import type { PropertyModules } from '../../../../../packages/entities/Property';
import { apiRawFetch } from '../../main/client';
import CalendarSharing from './CalendarSharing';
import ModuleConfiguration from './ModuleConfiguration';
import PropertyInvitedUsers from './PropertyInvitedUsers';

const COUNTRIES_QUERY = gql`
    query countries {
        countries {
            code
            name
        }
    }
`;

interface Property {
    id: number;
    name: string;
    minNights: number;
    currency: string;
    country: string;
    weekStartDay: 0 | 1;
    modules: PropertyModules;
    created: string;
    updated: string;
}

interface PropertySettingsDialogProps {
    open: boolean;
    property: Property | null;
    onClose: () => void;
    onSaved: (newPropertyId?: number) => void;
    onDelete?: (propertyId: number) => void;
}

const COMMON_CURRENCIES = [
    'EUR',
    'USD',
    'GBP',
    'HUF',
    'JPY',
    'CHF',
    'CAD',
    'AUD',
    'CNY',
    'SEK',
    'NOK',
    'DKK',
    'PLN',
    'CZK',
];

const PropertySettingsDialog = ({
    open,
    property,
    onClose,
    onSaved,
    onDelete,
}: PropertySettingsDialogProps) => {
    const [name, setName] = useState('');
    const [minNights, setMinNights] = useState('2');
    const [currency, setCurrency] = useState('EUR');
    const [country, setCountry] = useState('HU');
    const [weekStartDay, setWeekStartDay] = useState<0 | 1>(1);
    const [modules, setModules] = useState<PropertyModules>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [focusModuleId, setFocusModuleId] = useState<string | undefined>(undefined);
    const [currentTab, setCurrentTab] = useState(0);

    const { data: countriesData } = useQuery<{
        countries: { code: string; name: string }[];
    }>(COUNTRIES_QUERY);

    const countries = countriesData?.countries ?? [];
    const sortedCountries = countries
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name));

    const isEditing = property !== null;

    useEffect(() => {
        if (property) {
            setName(property.name);
            setMinNights(property.minNights.toString());
            setCurrency(property.currency);
            setCountry(property.country);
            setWeekStartDay(property.weekStartDay);
            setModules(property.modules ?? {});
        } else {
            setName('');
            setMinNights('2');
            setCurrency('EUR');
            setCountry('HU');
            setWeekStartDay(1);
            setModules({});
        }
        setError(null);
        setFocusModuleId(undefined);
        setCurrentTab(0);
    }, [property, open]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            setError('Property name is required');
            return;
        }

        const minNightsNum = parseInt(minNights, 10);
        if (isNaN(minNightsNum) || minNightsNum < 1) {
            setError('Minimum nights must be at least 1');
            return;
        }

        if (!currency || currency.length !== 3) {
            setError('Currency must be exactly 3 characters');
            return;
        }

        if (!country || country.length !== 2) {
            setError('Country is required');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const endpoint =
                isEditing && property
                    ? `api/properties/${property.id}`
                    : 'api/properties';

            const body = {
                name: name.trim(),
                minNights: minNightsNum,
                currency: currency.toUpperCase(),
                country: country.toUpperCase(),
                weekStartDay,
                modules,
            };

            const response = await apiRawFetch(endpoint, 'POST', body);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({})) as {
                    error?: string;
                    details?: { moduleId?: string };
                };
                const errorMessage = errorData.error ?? 'Failed to save property';
                setError(errorMessage);
                // If the error is related to a module, focus that module
                if (errorData.details?.moduleId) {
                    setFocusModuleId(errorData.details.moduleId);
                }
                return;
            }

            // Get the new property ID when creating
            if (!isEditing) {
                const data = await response.json() as { id: number };
                onSaved(data.id);
            } else {
                onSaved();
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    const handleDelete = () => {
        if (property && onDelete) {
            onDelete(property.id);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 0,
                    border: '4px solid #6366f1',
                    backgroundColor: '#1a1a2e',
                },
            }}
        >
            <form onSubmit={handleSubmit}>
                <DialogTitle
                    sx={{
                        borderBottom: isEditing ? 'none' : '3px solid #6366f1',
                        backgroundColor: '#0f0f23',
                        fontWeight: 900,
                        fontSize: '1.5rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2.5,
                    }}
                >
                    {isEditing ? 'Property Settings' : 'Create New Property'}
                </DialogTitle>
                {isEditing && (
                    <Box
                        sx={{
                            borderBottom: '3px solid #6366f1',
                            backgroundColor: '#0f0f23',
                        }}
                    >
                        <Tabs
                            value={currentTab}
                            onChange={(_, newValue) => setCurrentTab(newValue)}
                            sx={{
                                '& .MuiTab-root': {
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    fontSize: '0.875rem',
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#6366f1',
                                    height: 3,
                                },
                            }}
                        >
                            <Tab label="Settings" />
                            <Tab label="Access" />
                            <Tab label="Sharing" />
                        </Tabs>
                    </Box>
                )}
                <DialogContent
                    sx={{
                        backgroundColor: '#1a1a2e',
                        p: 0,
                    }}
                >
                    {/* Settings Tab (index 0) - shown for both create and edit, or when editing and on Settings tab */}
                    {(!isEditing || currentTab === 0) && (
                        <Stack sx={{ py: 3, px: 3 }} spacing={2.5}>
                            {error && (
                                <Alert
                                    severity="error"
                                    onClose={() => setError(null)}
                                    sx={{
                                        borderRadius: 0,
                                        border: '3px solid #ef4444',
                                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                        fontWeight: 600,
                                        '& .MuiAlert-icon': {
                                            color: '#ef4444',
                                        },
                                    }}
                                >
                                    {error}
                                </Alert>
                            )}
                            <TextField
                                label="Property Name"
                                type="text"
                                fullWidth
                                value={name}
                                onChange={e => setName(e.target.value)}
                                disabled={loading}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 0,
                                        '& input': {
                                            backgroundColor: '#0f0f23',
                                        },
                                        '& fieldset': {
                                            borderWidth: '2px',
                                            borderColor: '#374151',
                                        },
                                        '&:hover fieldset': {
                                            borderWidth: '2px',
                                            borderColor: '#6366f1',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderWidth: '3px',
                                            borderColor: '#6366f1',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        fontSize: '1rem',
                                        letterSpacing: '0.05em',
                                    },
                                }}
                            />
                            <TextField
                                label="Minimum Nights"
                                type="number"
                                fullWidth
                                value={minNights}
                                onChange={e => setMinNights(e.target.value)}
                                disabled={loading}
                                required
                                slotProps={{
                                    htmlInput: {
                                        min: 1,
                                    },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 0,
                                        '& input': {
                                            backgroundColor: '#0f0f23',
                                        },
                                        '& fieldset': {
                                            borderWidth: '2px',
                                            borderColor: '#374151',
                                        },
                                        '&:hover fieldset': {
                                            borderWidth: '2px',
                                            borderColor: '#6366f1',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderWidth: '3px',
                                            borderColor: '#6366f1',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        fontSize: '1rem',
                                        letterSpacing: '0.05em',
                                    },
                                }}
                            />
                            <Autocomplete
                                freeSolo
                                value={currency}
                                onChange={(_event, newValue) => {
                                    if (newValue && typeof newValue === 'string') {
                                        setCurrency(newValue.toUpperCase());
                                    }
                                }}
                                onInputChange={(_event, newValue) => {
                                    setCurrency(newValue.toUpperCase());
                                }}
                                options={COMMON_CURRENCIES}
                                disabled={loading}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label="Currency"
                                        required
                                        slotProps={{
                                            htmlInput: {
                                                ...params.inputProps,
                                                maxLength: 3,
                                            },
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 0,
                                                '& input': {
                                                    backgroundColor: '#0f0f23',
                                                },
                                                '& fieldset': {
                                                    borderWidth: '2px',
                                                    borderColor: '#374151',
                                                },
                                                '&:hover fieldset': {
                                                    borderWidth: '2px',
                                                    borderColor: '#6366f1',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderWidth: '3px',
                                                    borderColor: '#6366f1',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                fontSize: '1rem',
                                                letterSpacing: '0.05em',
                                            },
                                        }}
                                    />
                                )}
                            />
                            <Autocomplete
                                value={
                                    sortedCountries.find(c => c.code === country) ??
                                    null
                                }
                                onChange={(_event, newValue) => {
                                    if (newValue) {
                                        setCountry(newValue.code);
                                    }
                                }}
                                options={sortedCountries}
                                getOptionLabel={option =>
                                    `${option.name} (${option.code})`
                                }
                                isOptionEqualToValue={(option, value) =>
                                    option.code === value.code
                                }
                                disabled={loading}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label="Country"
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 0,
                                                '& input': {
                                                    backgroundColor: '#0f0f23',
                                                },
                                                '& fieldset': {
                                                    borderWidth: '2px',
                                                    borderColor: '#374151',
                                                },
                                                '&:hover fieldset': {
                                                    borderWidth: '2px',
                                                    borderColor: '#6366f1',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderWidth: '3px',
                                                    borderColor: '#6366f1',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                fontSize: '1rem',
                                                letterSpacing: '0.05em',
                                            },
                                        }}
                                    />
                                )}
                            />
                            <TextField
                                select
                                label="Week Starts On"
                                fullWidth
                                value={weekStartDay}
                                onChange={e =>
                                    setWeekStartDay(
                                        parseInt(e.target.value, 10) as 0 | 1
                                    )
                                }
                                disabled={loading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 0,
                                        '& .MuiSelect-select': {
                                            backgroundColor: '#0f0f23',
                                        },
                                        '& fieldset': {
                                            borderWidth: '2px',
                                            borderColor: '#374151',
                                        },
                                        '&:hover fieldset': {
                                            borderWidth: '2px',
                                            borderColor: '#6366f1',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderWidth: '3px',
                                            borderColor: '#6366f1',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        fontSize: '1rem',
                                        letterSpacing: '0.05em',
                                    },
                                }}
                            >
                                <MenuItem value={1}>Monday (ISO 8601)</MenuItem>
                                <MenuItem value={0}>Sunday</MenuItem>
                            </TextField>
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    color: '#e5e7eb',
                                    mt: 2,
                                }}
                            >
                                Modules
                            </Typography>
                            <ModuleConfiguration
                                modules={modules}
                                onChange={setModules}
                                disabled={loading}
                                focusModuleId={focusModuleId}
                                onModuleSelect={() => setFocusModuleId(undefined)}
                                propertyCountry={country}
                            />
                        </Stack>
                    )}

                    {/* Access Tab (index 1) - only when editing */}
                    {isEditing && property && currentTab === 1 && (
                        <Stack sx={{ py: 3, px: 3 }} spacing={2.5}>
                            <Alert
                                severity="info"
                                sx={{
                                    borderRadius: 0,
                                    border: '3px solid #6366f1',
                                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                    fontWeight: 600,
                                    '& .MuiAlert-icon': {
                                        color: '#6366f1',
                                    },
                                }}
                            >
                                Changes take effect immediately
                            </Alert>
                            <PropertyInvitedUsers
                                propertyId={property.id}
                                propertyName={property.name}
                                disabled={loading}
                            />
                        </Stack>
                    )}

                    {/* Sharing Tab (index 2) - only when editing */}
                    {isEditing && property && currentTab === 2 && (
                        <Stack sx={{ py: 3, px: 3 }} spacing={2.5}>
                            <Alert
                                severity="info"
                                sx={{
                                    borderRadius: 0,
                                    border: '3px solid #6366f1',
                                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                    fontWeight: 600,
                                    '& .MuiAlert-icon': {
                                        color: '#6366f1',
                                    },
                                }}
                            >
                                Changes take effect immediately
                            </Alert>
                            <CalendarSharing
                                propertyId={property.id}
                                disabled={loading}
                            />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{
                        borderTop: '3px solid #374151',
                        backgroundColor: '#0f0f23',
                        p: 3,
                        gap: 1.5,
                        justifyContent:
                            isEditing && onDelete && currentTab === 0
                                ? 'space-between'
                                : 'flex-end',
                    }}
                >
                    {isEditing && onDelete && currentTab === 0 && (
                        <Button
                            onClick={handleDelete}
                            disabled={loading}
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            sx={{
                                borderRadius: 0,
                                borderWidth: '2px',
                                borderColor: 'error.main',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    borderWidth: '2px',
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                },
                            }}
                        >
                            Delete
                        </Button>
                    )}
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <Button
                            onClick={handleClose}
                            disabled={loading}
                            variant="outlined"
                            sx={{
                                borderRadius: 0,
                                borderWidth: '2px',
                                borderColor: '#374151',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                    backgroundColor: 'rgba(55, 65, 81, 0.1)',
                                },
                            }}
                        >
                            {(!isEditing || currentTab === 0) ? 'Cancel' : 'Close'}
                        </Button>
                        {(!isEditing || currentTab === 0) && (
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={loading}
                                sx={{
                                    borderRadius: 0,
                                    border: '2px solid #6366f1',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    px: 3,
                                    py: 1,
                                }}
                            >
                                {(() => {
                                    if (loading) return 'Saving...';
                                    return isEditing ? 'Update' : 'Create';
                                })()}
                            </Button>
                        )}
                    </div>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default PropertySettingsDialog;
